'use client'

import { cn } from '@/lib/utils'
import { Icon } from '@/ui/components'
import { memo } from 'react'
import { twMerge } from 'tailwind-merge'
import { ButtonIconProps } from './type'

const ButtonIcon = ({
  size = 'l',
  variant = 'filled',
  mode = 'default',
  icon = 'material-symbols:close',
  showPointer = false,
  className,
  animHover,
  ...props
}: ButtonIconProps) => {
  const variantAndMode = {
    filled: {
      modes: {
        default:
          'bg-neutral-200 text-neutral-600 hover:bg-neutral-300 disabled:opacity-20',
        invert:
          'bg-neutral-200 text-neutral-600 hover:bg-neutral-300 disabled:opacity-20'
      },
      pointer:
        'border-t-neutral-200 group-hover/buttonIcon:border-t-neutral-300'
    },
    outline: {
      modes: {
        default:
          'border border-neutral-300 text-primary-400 hover:bg-neutral-300 disabled:opacity-40',
        invert:
          'border border-white bg-white/0 text-white hover:bg-alpha-white-100 disabled:opacity-40'
      },
      pointer: 'hidden'
    },
    ghost: {
      modes: {
        default: 'text-neutral-600 hover:text-primary-300 disabled:opacity-40',
        invert: 'text-neutral-200 hover:bg-primary-300 disabled:opacity-40'
      },
      pointer: 'hidden'
    },
    'default-alpha': {
      modes: {
        default:
          'text-neutral-100 border border-neutral-100 hover:bg-alpha-white-100 disabled:opacity-40',
        invert:
          'text-neutral-100 border border-neutral-100 hover:bg-alpha-white-100 disabled:opacity-40'
      },
      pointer: 'hidden'
    },
    'filled-alpha': {
      modes: {
        default:
          'bg-alpha-white-400 text-neutral-600 hover:bg-neutral-100 disabled:opacity-40',
        invert:
          'bg-alpha-white-400 text-neutral-600 hover:bg-neutral-100 disabled:opacity-40'
      },
      pointer:
        'border-t-alpha-white-400 group-hover/buttonIcon:border-t-neutral-100'
    },
    'filled-accent': {
      modes: {
        default:
          'bg-accent-300 text-neutral-100 hover:bg-accent-200 disabled:opacity-40',
        invert:
          'bg-neutral-100 text-accent-300 hover:bg-accent-100 disabled:opacity-40'
      },
      pointer:
        'border-t-alpha-white-400 group-hover/buttonIcon:border-t-neutral-100'
    }
  }

  const sizeClass = twMerge(
    size === 's' && 'w-8 h-8',
    size === 'm' && 'w-10 h-10',
    size === 'l' && 'w-12 h-12'
  )

  const classCompser = cn(
    'group/buttonIcon global-focus cursor-pointer relative inline-flex items-center justify-center transition-all duration-200 disabled:pointer-events-none disabled:opacity-20',
    variantAndMode[variant].modes[mode],
    sizeClass,
    className
  )

  const iconClassName = twMerge(
    'items-center justify-center',
    size === 'l' && 'w-5 h-5',
    size === 'm' && 'w-5 h-5',
    size === 's' && 'w-3.5'
  )

  return (
    <button className={classCompser} {...props}>
      {icon && (
        <div className={iconClassName} data-icon>
          {typeof icon === 'string' ? (
            <Icon className="h-full w-full" icon={icon} />
          ) : (
            icon
          )}

          {showPointer && (
            <div
              className={cn(
                'absolute top-full left-1/2 -ml-10 h-0 w-0 border-t-10 border-r-10 border-l-10 border-r-transparent border-l-transparent transition-all duration-200',
                variantAndMode[variant].pointer
              )}
            ></div>
          )}
        </div>
      )}
    </button>
  )
}

export default memo(ButtonIcon)
